import { errorFromResponse, NetworkError } from "./errors.js";
import { devIdentityHeaders } from "./devActAsClient.js";

const path = projectId => `/api/projects/${encodeURIComponent(projectId)}/media`;
export async function mediaRequest(url, options = {}) {
  let response;
  try { response = await fetch(url, { credentials: "same-origin", ...options }); }
  catch (err) { if (err.name === "AbortError") throw err; throw new NetworkError(err.message); }
  if (!response.ok) throw await errorFromResponse(response);
  return response.json();
}
const request = mediaRequest;
export async function getProjectMedia(projectId) {
  const result = await request(path(projectId));
  if (!Array.isArray(result?.items) || typeof result?.capabilities?.canManage !== "boolean") throw new Error("The server returned an invalid media list.");
  return { ...result, coverMediaId: result.coverMediaId ?? null, effectiveCover: result.effectiveCover && typeof result.effectiveCover === "object" ? result.effectiveCover : null };
}
export function uploadProjectMedia(projectId, file, weekDate, onProgress, signal) {
  return uploadMediaTo(path(projectId), file, weekDate, onProgress, signal);
}
export function uploadMediaTo(url, file, weekDate, onProgress, signal) {
  if (signal?.aborted) return Promise.reject(new DOMException("Upload cancelled", "AbortError"));
  const body = new FormData();
  body.append("file", file);
  if (weekDate) body.append("weekDate", weekDate);
  // Fetch has no upload progress events. Keep the same authenticated endpoint,
  // multipart boundary and error classification when a caller needs progress.
  if (typeof onProgress === "function") return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;
    const abort = () => xhr.abort();
    const finish = (callback, value) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", abort);
      callback(value);
    };
    xhr.open("POST", url);
    xhr.withCredentials = true;
    devIdentityHeaders(url).forEach((value, name) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = event => !settled && onProgress({
      loaded: event.loaded, total: event.total, lengthComputable: event.lengthComputable,
    });
    xhr.onerror = () => finish(reject, new NetworkError("Network request failed"));
    xhr.onabort = () => finish(reject, new DOMException("Upload cancelled", "AbortError"));
    xhr.ontimeout = () => finish(reject, new NetworkError("Upload timed out"));
    xhr.onload = async () => {
      if (settled) return;
      let payload;
      try { payload = JSON.parse(xhr.responseText); } catch { /* classified below */ }
      if (xhr.status < 200 || xhr.status >= 300) {
        finish(reject, await errorFromResponse({
          status: xhr.status, statusText: xhr.statusText, json: async () => payload,
        }));
        return;
      }
      if (!payload || typeof payload !== "object") { finish(reject, new Error("The server returned an invalid upload response.")); return; }
      finish(resolve, payload);
    };
    signal?.addEventListener("abort", abort, { once: true });
    xhr.send(body);
  });
  return request(url, { method: "POST", body, signal });
}
export function updateProjectMedia(projectId, mediaId, caption, weekDate) {
  return request(`${path(projectId)}/${encodeURIComponent(mediaId)}`, {
    method: "PATCH", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ caption, ...(weekDate ? { weekDate } : {}) }),
  });
}
export function removeProjectMedia(projectId, mediaId, weekDate) {
  return request(`${path(projectId)}/${encodeURIComponent(mediaId)}`, {
    method: "DELETE", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...(weekDate ? { weekDate } : {}) }),
  });
}
export function restoreProjectMedia(projectId, mediaId, undoToken, weekDate) {
  return request(`${path(projectId)}/${encodeURIComponent(mediaId)}/restore`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ undoToken, ...(weekDate ? { weekDate } : {}) }),
  });
}
export function reorderProjectMedia(projectId, ids, weekDate) {
  return request(`${path(projectId)}/order`, {
    method: "PUT", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, ...(weekDate ? { weekDate } : {}) }),
  });
}
export function setProjectCover(projectId, mediaId, weekDate) {
  return request(`${path(projectId)}/cover`, {
    method: "PATCH", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mediaId: mediaId ?? null, ...(weekDate ? { weekDate } : {}) }),
  });
}

// In-app media change signal so every media section and the drawer header
// for the same project refetch without a reload. source lets the emitter skip.
export const PROJECT_MEDIA_EVENT = "places:project-media-changed";
export function emitProjectMediaChanged(projectId, source = null) {
  if (typeof window === "undefined" || projectId == null) return;
  window.dispatchEvent(new CustomEvent(PROJECT_MEDIA_EVENT, { detail: { projectId: String(projectId), source } }));
}
export function onProjectMediaChanged(projectId, handler) {
  if (typeof window === "undefined") return () => {};
  const fn = (e) => { if (String(e.detail?.projectId) === String(projectId)) handler(e.detail); };
  window.addEventListener(PROJECT_MEDIA_EVENT, fn);
  return () => window.removeEventListener(PROJECT_MEDIA_EVENT, fn);
}
